
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "chart.js/auto";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./adminDashboard.css";
import "./dashboardmain.css";
import "./horizonTable.css";
import { parseSiteRegisterDate } from "./adminEditModalTheme";

const THEME = "#8b1634";
const TICKET_OPEN = "#f59e0b";
const TICKET_IN_PROGRESS = "#2563eb";
const TICKET_CLOSED = "#16a34a";

function monthKey(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabelFromKey(key) {
  const [year, month] = String(key).split("-");
  const d = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(d.getTime())) return key;
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
}

function parseTimeToMinutes(value) {
  const m = String(value || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
}

function shiftDurationHours(onDutyTime, offDutyTime) {
  const start = parseTimeToMinutes(onDutyTime);
  const end = parseTimeToMinutes(offDutyTime);
  if (start == null || end == null) return null;
  let diff = end - start;
  if (diff < 0) diff += 24 * 60;
  return diff / 60;
}

function getEntityId(value) {
  if (!value) return "";
  if (typeof value === "object") {
    return String(value._id || value.id || "").trim();
  }
  return String(value).trim();
}

function toYmdUtc(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

function addDaysToYmd(ymd, days) {
  const parts = String(ymd).split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return ymd;
  const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  d.setUTCDate(d.getUTCDate() + days);
  return toYmdUtc(d);
}

function getUpcomingDateWindowYmds(dayCount = 14) {
  const startYmd = toYmdUtc(new Date());
  return Array.from({ length: dayCount }, (_, index) => addDaysToYmd(startYmd, index));
}

function formatSheetDateLabel(dateLike) {
  if (!dateLike) return "—";
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const ymd = typeof dateLike === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateLike) ? dateLike : toYmdUtc(dateLike);
  const match = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "—";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return "—";
  return `${String(day).padStart(2, "0")} ${monthNames[month - 1]} ${year}`;
}

function standbyCoversYmd(standby, ymd) {
  if (!standby || !ymd) return false;
  const selectedDates = Array.isArray(standby.selectedDates)
    ? standby.selectedDates.map((d) => String(d).trim()).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    : [];
  if (selectedDates.length > 0) return selectedDates.includes(ymd);
  const fromYmd = toYmdUtc(standby.dateFrom);
  const toYmd = toYmdUtc(standby.dateTo || standby.dateFrom);
  if (!fromYmd || !toYmd) return false;
  return ymd >= fromYmd && ymd <= toYmd;
}

function formatWorkerName(worker) {
  if (!worker) return "Unassigned";
  if (typeof worker === "object") {
    const name = `${worker.firstName || ""} ${worker.lastName || ""}`.trim();
    return name || worker.email || "Unnamed";
  }
  return "Worker";
}

function dateToLocalYMD(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseWorkforceFilterDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const slashOrDash = parseSiteRegisterDate(value);
  if (slashOrDash) return slashOrDash;
  if (typeof value === "string") {
    const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const NON_WORKING_DAY_TYPES = new Set(["sick", "holiday", "college"]);

const DAY_TYPE_LABELS = {
  sick: "Sick Day",
  holiday: "Holiday",
  college: "College Day",
};

const DAY_TYPE_COLORS = {
  sick: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
  holiday: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
  college: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
};

function DashboardMain() {
  const [assets, setAssets] = useState([]);
  const [labels, setLabels] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [fatigueSheets, setFatigueSheets] = useState([]);
  const [fatigueShifts, setFatigueShifts] = useState([]);
  const [fatigueExceedances, setFatigueExceedances] = useState([]);
  const [standbysToday, setStandbysToday] = useState([]);
  const [workforceFilterDate, setWorkforceFilterDate] = useState(() => dateToLocalYMD(new Date()));
  const [workforceFilterWorkerId, setWorkforceFilterWorkerId] = useState("");
  const [loading, setLoading] = useState(true);

  const role = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user"));
      return String(u?.role || "").toLowerCase();
    } catch {
      return "";
    }
  }, []);
  const hideAssetInsights = role === "manager";

  const lifecycleChartRef = useRef(null);
  const sitesChartRef = useRef(null);
  const trendChartRef = useRef(null);
  const ticketChartRef = useRef(null);
  const fatigueSheetStatusChartRef = useRef(null);
  const fatigueShiftPatternChartRef = useRef(null);
  const fatigueExceedanceTypeChartRef = useRef(null);
  const fatigueRiskBandChartRef = useRef(null);

  const lifecycleChart = useRef(null);
  const sitesChart = useRef(null);
  const trendChart = useRef(null);
  const ticketChart = useRef(null);
  const fatigueSheetStatusChart = useRef(null);
  const fatigueShiftPatternChart = useRef(null);
  const fatigueExceedanceTypeChart = useRef(null);
  const fatigueRiskBandChart = useRef(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "";
        const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const [assetsRes, labelsRes, ticketsRes, fatigueSheetsRes, fatigueShiftsRes, fatigueExceedanceRes, standbyRes] = await Promise.all([
          fetch(`${baseUrl}/api/asset`, { headers }),
          fetch(`${baseUrl}/api/asset-management/labels`, { headers }),
          fetch(`${baseUrl}/api/asset/tickets`, { headers }),
          fetch(`${baseUrl}/api/fatigue-work-sheets`, { headers }),
          fetch(`${baseUrl}/api/fatigue-shifts`, { headers }),
          fetch(`${baseUrl}/api/fatigue-exceedance-forms/all`, { headers }),
          fetch(`${baseUrl}/api/on-call-standby`, { headers }),
        ]);
        const [assetsData, labelsData, ticketsData, fatigueSheetsData, fatigueShiftsData, fatigueExceedanceData, standbyData] = await Promise.all([
          assetsRes.json().catch(() => ({})),
          labelsRes.json().catch(() => ({})),
          ticketsRes.json().catch(() => ({})),
          fatigueSheetsRes.json().catch(() => ({})),
          fatigueShiftsRes.json().catch(() => ({})),
          fatigueExceedanceRes.json().catch(() => ({})),
          standbyRes.json().catch(() => ({})),
        ]);
        if (!alive) return;
        const loadedAssets = assetsRes.ok && assetsData?.success && Array.isArray(assetsData.data) ? assetsData.data : [];
        const loadedLabels = labelsRes.ok && labelsData?.success && Array.isArray(labelsData.data) ? labelsData.data : [];
        const loadedTickets = ticketsRes.ok && ticketsData?.success && Array.isArray(ticketsData.data) ? ticketsData.data : [];
        const loadedSheets =
          fatigueSheetsRes.ok && fatigueSheetsData?.success && Array.isArray(fatigueSheetsData.data) ? fatigueSheetsData.data : [];
        const loadedShifts =
          fatigueShiftsRes.ok && fatigueShiftsData?.success && Array.isArray(fatigueShiftsData.data) ? fatigueShiftsData.data : [];
        const loadedExceedances =
          fatigueExceedanceRes.ok && fatigueExceedanceData?.success && Array.isArray(fatigueExceedanceData.data)
            ? fatigueExceedanceData.data
            : [];

        let scopedShifts = loadedShifts;
        let scopedExceedances = loadedExceedances;
        if (role === "manager") {
          const allowedSheetIds = new Set(loadedSheets.map((s) => getEntityId(s?._id || s?.id)).filter(Boolean));
          const allowedWorkerIds = new Set(
            loadedSheets
              .flatMap((s) => (Array.isArray(s?.workerIds) ? s.workerIds : []))
              .map((w) => getEntityId(w))
              .filter(Boolean)
          );
          scopedShifts = loadedShifts.filter((row) => {
            const rowSheetId = getEntityId(row?.workSheetId);
            const rowWorkerId = getEntityId(row?.appUserId);
            return (rowSheetId && allowedSheetIds.has(rowSheetId)) || (rowWorkerId && allowedWorkerIds.has(rowWorkerId));
          });
          scopedExceedances = loadedExceedances.filter((row) => {
            const rowSheetId = getEntityId(row?.workSheetId);
            const rowWorkerId = getEntityId(row?.appUserId);
            return (rowSheetId && allowedSheetIds.has(rowSheetId)) || (rowWorkerId && allowedWorkerIds.has(rowWorkerId));
          });
        }

        setAssets(loadedAssets);
        setLabels(loadedLabels);
        setTickets(loadedTickets);
        const loadedStandbys =
          standbyRes.ok && standbyData?.success && Array.isArray(standbyData.data) ? standbyData.data : [];

        setFatigueSheets(loadedSheets);
        setFatigueShifts(scopedShifts);
        setFatigueExceedances(scopedExceedances);
        setStandbysToday(loadedStandbys);
      } catch {
        if (!alive) return;
        setAssets([]);
        setLabels([]);
        setTickets([]);
        setFatigueSheets([]);
        setFatigueShifts([]);
        setFatigueExceedances([]);
        setStandbysToday([]);
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  const metrics = useMemo(() => {
    const rows = Array.isArray(assets) ? assets : [];
    const labelRows = Array.isArray(labels) ? labels : [];
    const ticketRows = Array.isArray(tickets) ? tickets : [];

    const retired = rows.filter((a) => String(a.assetStatus || "").toLowerCase() === "retired").length;
    const fault = rows.filter((a) => String(a.deviceCondition || "").toLowerCase() === "fault").length;
    const maintenance = rows.filter((a) => String(a.deviceCondition || "").toLowerCase() === "maintenance required").length;
    const good = Math.max(0, rows.length - retired - fault - maintenance);
    const unassignedLabels = labelRows.filter((x) => String(x.status || "").toLowerCase() === "unassigned").length;

    const openTickets = ticketRows.filter((t) => String(t.status || "").toLowerCase() === "open").length;
    const inProgressTickets = ticketRows.filter((t) => String(t.status || "").toLowerCase() === "in_progress").length;

    const bySite = {};
    rows.forEach((a) => {
      const site = String(a.siteName || "Unknown").trim() || "Unknown";
      bySite[site] = (bySite[site] || 0) + 1;
    });
    const topSites = Object.entries(bySite)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);

    const byClient = {};
    rows.forEach((a) => {
      const client = String(a.clientName || "").trim();
      if (!client) return;
      byClient[client] = (byClient[client] || 0) + 1;
    });
    const topClients = Object.entries(byClient)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    const now = new Date();
    const monthKeys = [];
    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      monthKeys.push(monthKey(d));
    }
    const byMonth = Object.fromEntries(monthKeys.map((k) => [k, 0]));
    rows.forEach((a) => {
      const key = monthKey(a.createdAt);
      if (byMonth[key] === undefined) return;
      byMonth[key] += 1;
    });

    const ticketStatusCounts = {
      open: 0,
      inProgress: 0,
      closed: 0,
    };
    const ticketSourceCounts = {
      web: 0,
      app: 0,
    };
    ticketRows.forEach((t) => {
      const status = String(t.status || "").toLowerCase();
      if (status === "open") ticketStatusCounts.open += 1;
      if (status === "in_progress") ticketStatusCounts.inProgress += 1;
      if (status === "closed") ticketStatusCounts.closed += 1;
      const source = String(t.ticketSource || "").toLowerCase();
      if (source === "web") ticketSourceCounts.web += 1;
      if (source === "app") ticketSourceCounts.app += 1;
    });

    return {
      totalAssets: rows.length,
      good,
      fault,
      maintenance,
      retired,
      unassignedLabels,
      openTickets,
      inProgressTickets,
      topSites,
      topClients,
      trendLabels: monthKeys.map(monthLabelFromKey),
      trendValues: monthKeys.map((k) => byMonth[k]),
      ticketStatusCounts,
      ticketSourceCounts,
    };
  }, [assets, labels, tickets]);

  const fatigueMetrics = useMemo(() => {
    const sheets = Array.isArray(fatigueSheets) ? fatigueSheets : [];
    const shifts = Array.isArray(fatigueShifts) ? fatigueShifts : [];
    const exceedances = Array.isArray(fatigueExceedances) ? fatigueExceedances : [];

    const activeSheets = sheets.filter((s) => String(s.status || "").toLowerCase() === "active").length;
    const inactiveSheets = sheets.filter((s) => String(s.status || "").toLowerCase() === "inactive").length;

    const workerSheetSet = new Set();
    sheets.forEach((s) => {
      const workerIds = Array.isArray(s.workerIds) ? s.workerIds : [];
      workerIds.forEach((w) => {
        const id = typeof w === "object" ? String(w?._id || "") : String(w || "");
        if (id) workerSheetSet.add(id);
      });
    });

    const workerShiftMap = {};
    const dayNight = { day: 0, night: 0 };
    const dayNightHours = { day: 0, night: 0 };
    const dayNightDurationCount = { day: 0, night: 0 };
    let totalDurationHours = 0;
    let durationCount = 0;
    shifts.forEach((s) => {
      const sid = typeof s.appUserId === "object" ? String(s.appUserId?._id || "") : String(s.appUserId || "");
      const sname =
        typeof s.appUserId === "object"
          ? `${s.appUserId?.firstName || ""} ${s.appUserId?.lastName || ""}`.trim() || s.appUserId?.email || "Unknown"
          : "Unknown";
      if (sid) {
        if (!workerShiftMap[sid]) workerShiftMap[sid] = { name: sname, count: 0 };
        workerShiftMap[sid].count += 1;
      }
      const stype = String(s.shiftType || "").toLowerCase();
      if (stype === "night") dayNight.night += 1;
      else dayNight.day += 1;
      const dur = shiftDurationHours(s.onDutyTime, s.offDutyTime);
      if (dur != null) {
        totalDurationHours += dur;
        durationCount += 1;
        if (stype === "night") {
          dayNightHours.night += dur;
          dayNightDurationCount.night += 1;
        } else {
          dayNightHours.day += dur;
          dayNightDurationCount.day += 1;
        }
      }
    });
    const avgShiftHours = durationCount ? Number((totalDurationHours / durationCount).toFixed(1)) : 0;

    const exceedanceStatus = { pending: 0, approved: 0, rejected: 0 };
    const exceedanceType = {};
    const exceedanceByWorker = {};
    exceedances.forEach((e) => {
      const status = String(e.status || "").toLowerCase();
      if (status === "pending") exceedanceStatus.pending += 1;
      else if (status === "approved") exceedanceStatus.approved += 1;
      else if (status === "rejected") exceedanceStatus.rejected += 1;

      const type = String(e.type || e.level || "Unknown").trim() || "Unknown";
      exceedanceType[type] = (exceedanceType[type] || 0) + 1;

      const wid = typeof e.appUserId === "object" ? String(e.appUserId?._id || "") : String(e.appUserId || "");
      const wname =
        typeof e.appUserId === "object"
          ? `${e.appUserId?.firstName || ""} ${e.appUserId?.lastName || ""}`.trim() || e.appUserId?.email || "Unknown"
          : "Unknown";
      if (wid) {
        if (!exceedanceByWorker[wid]) exceedanceByWorker[wid] = { name: wname, count: 0 };
        exceedanceByWorker[wid].count += 1;
      }
    });

    const entries = sheets.flatMap((s) => (Array.isArray(s.entries) ? s.entries : []));
    const riskBands = { low: 0, medium: 0, high: 0, critical: 0 };
    let fatigueIdxTotal = 0;
    let fatigueIdxCount = 0;
    let riskIdxTotal = 0;
    let riskIdxCount = 0;
    let onRestCount = 0;
    entries.forEach((entry) => {
      if (entry?.isOnRest) onRestCount += 1;
      const fi = Number(entry?.fatigueIndex);
      if (!Number.isNaN(fi) && fi > 0) {
        fatigueIdxTotal += fi;
        fatigueIdxCount += 1;
      }
      const ri = Number(entry?.riskIndex);
      if (!Number.isNaN(ri) && ri > 0) {
        riskIdxTotal += ri;
        riskIdxCount += 1;
        if (ri < 1) riskBands.low += 1;
        else if (ri < 1.3) riskBands.medium += 1;
        else if (ri < 1.6) riskBands.high += 1;
        else riskBands.critical += 1;
      }
    });

    const topWorkersByShifts = Object.values(workerShiftMap).sort((a, b) => b.count - a.count).slice(0, 6);
    const topWorkersByExceedance = Object.values(exceedanceByWorker).sort((a, b) => b.count - a.count).slice(0, 6);
    const exceedanceTypeRows = Object.entries(exceedanceType).sort((a, b) => b[1] - a[1]);

    return {
      totalSheets: sheets.length,
      activeSheets,
      inactiveSheets,
      totalShifts: shifts.length,
      uniqueWorkersInSheets: workerSheetSet.size,
      avgShiftHours,
      totalExceedances: exceedances.length,
      pendingExceedances: exceedanceStatus.pending,
      approvedExceedances: exceedanceStatus.approved,
      rejectedExceedances: exceedanceStatus.rejected,
      avgFatigueIndex: fatigueIdxCount ? Number((fatigueIdxTotal / fatigueIdxCount).toFixed(2)) : 0,
      avgRiskIndex: riskIdxCount ? Number((riskIdxTotal / riskIdxCount).toFixed(2)) : 0,
      onRestCount,
      dayNight,
      dayNightHours,
      dayNightDurationCount,
      riskBands,
      topWorkersByShifts,
      topWorkersByExceedance,
      exceedanceTypeRows,
    };
  }, [fatigueSheets, fatigueShifts, fatigueExceedances]);

  const workforceData = useMemo(() => {
    const windowYmds = getUpcomingDateWindowYmds(14);
    const windowYmdSet = new Set(windowYmds);
    const rangeLabel = `${formatSheetDateLabel(windowYmds[0])} – ${formatSheetDateLabel(windowYmds[windowYmds.length - 1])}`;

    const standbyRows = [];
    for (const item of Array.isArray(standbysToday) ? standbysToday : []) {
      if (String(item.status || "").toLowerCase() !== "active") continue;
      const workerId = getEntityId(item.workerId);
      for (const ymd of windowYmds) {
        if (!standbyCoversYmd(item, ymd)) continue;
        standbyRows.push({
          id: `${item._id || item.id}-${ymd}`,
          workerId,
          workerName: formatWorkerName(item.workerId),
          siteName: item.siteId?.siteName || "—",
          siteLocation: item.siteId?.siteLocation || "",
          dateYmd: ymd,
          dateLabel: formatSheetDateLabel(ymd),
        });
      }
    }
    standbyRows.sort((a, b) => a.dateYmd.localeCompare(b.dateYmd) || a.workerName.localeCompare(b.workerName));

    const holidayRows = [];
    const workerMap = new Map();
    const sheets = Array.isArray(fatigueSheets) ? fatigueSheets : [];
    for (const sheet of sheets) {
      if (String(sheet.status || "").toLowerCase() === "inactive") continue;
      // Holiday/sick/college days are booked to Taylors HQ, not to the sheet's customer
      // site, so the entry's own resolved name wins. The sheet is only a fallback for
      // entries written before that field existed.
      const sheetSiteName = sheet.siteId?.siteName || "—";
      const sheetWorkers = Array.isArray(sheet.workerIds) ? sheet.workerIds : [];
      for (const worker of sheetWorkers) {
        const workerId = getEntityId(worker);
        if (!workerId) continue;
        workerMap.set(workerId, formatWorkerName(worker));
      }
      const entries = Array.isArray(sheet.entries) ? sheet.entries : [];
      for (const entry of entries) {
        const dayType = String(entry.dayType || "working").toLowerCase();
        if (!NON_WORKING_DAY_TYPES.has(dayType) || entry.isOnRest) continue;
        const entryYmd = toYmdUtc(entry.date);
        if (!windowYmdSet.has(entryYmd)) continue;
        let worker = entry.workerId;
        if (!worker && sheetWorkers.length === 1) worker = sheetWorkers[0];
        const workerId = getEntityId(worker);
        if (workerId) workerMap.set(workerId, formatWorkerName(worker));
        holidayRows.push({
          id: String(entry._id || `${getEntityId(sheet._id)}-${entryYmd}-${dayType}`),
          workerId,
          workerName: formatWorkerName(worker),
          dayType,
          dayTypeLabel: DAY_TYPE_LABELS[dayType] || dayType,
          siteName:
            (typeof entry.siteName === "string" && entry.siteName.trim()) ||
            (entry.siteId === null ? "—" : sheetSiteName),
          reason: entry.holidayTypeReason || "",
          sheetName: sheet.name || "",
          dateYmd: entryYmd,
          dateLabel: formatSheetDateLabel(entryYmd),
        });
      }
    }
    for (const item of Array.isArray(standbysToday) ? standbysToday : []) {
      const workerId = getEntityId(item.workerId);
      if (!workerId) continue;
      workerMap.set(workerId, formatWorkerName(item.workerId));
    }
    holidayRows.sort((a, b) => a.dateYmd.localeCompare(b.dateYmd) || a.workerName.localeCompare(b.workerName));

    const workers = Array.from(workerMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return { rangeLabel, standbyRows, holidayRows, workers, windowYmds };
  }, [standbysToday, fatigueSheets]);

  const workforceToday = useMemo(() => {
    let standbyRows = workforceData.standbyRows;
    let holidayRows = workforceData.holidayRows;
    if (workforceFilterDate) {
      standbyRows = standbyRows.filter((row) => row.dateYmd === workforceFilterDate);
      holidayRows = holidayRows.filter((row) => row.dateYmd === workforceFilterDate);
    }
    if (workforceFilterWorkerId) {
      standbyRows = standbyRows.filter((row) => row.workerId === workforceFilterWorkerId);
      holidayRows = holidayRows.filter((row) => row.workerId === workforceFilterWorkerId);
    }
    return {
      rangeLabel: workforceData.rangeLabel,
      standbyRows,
      holidayRows,
      workers: workforceData.workers,
    };
  }, [workforceData, workforceFilterDate, workforceFilterWorkerId]);

  const hasAssetLifecycleData = (metrics.good + metrics.fault + metrics.maintenance + metrics.retired) > 0;
  const hasTopSitesData = metrics.topSites.some((x) => Number(x?.[1] || 0) > 0);
  const hasAssetTrendData = metrics.trendValues.some((v) => Number(v || 0) > 0);
  const hasTicketStatusData =
    (metrics.ticketStatusCounts.open + metrics.ticketStatusCounts.inProgress + metrics.ticketStatusCounts.closed) > 0;

  const hasFatigueSheetStatusData = (fatigueMetrics.activeSheets + fatigueMetrics.inactiveSheets) > 0;
  const hasFatigueShiftPatternData = (fatigueMetrics.dayNight.day + fatigueMetrics.dayNight.night) > 0;
  const hasFatigueExceedanceTypeData = fatigueMetrics.exceedanceTypeRows.some((x) => Number(x?.[1] || 0) > 0);
  const hasFatigueRiskBandData =
    (fatigueMetrics.riskBands.low +
      fatigueMetrics.riskBands.medium +
      fatigueMetrics.riskBands.high +
      fatigueMetrics.riskBands.critical) > 0;

  const visibleAssetChartCount = [
    hasAssetLifecycleData,
    hasTopSitesData,
    hasAssetTrendData,
    hasTicketStatusData,
  ].filter(Boolean).length;

  const visibleFatigueChartCount = [
    hasFatigueSheetStatusData,
    hasFatigueShiftPatternData,
    hasFatigueExceedanceTypeData,
    hasFatigueRiskBandData,
  ].filter(Boolean).length;

  const chartColClass = (count) => (count <= 1 ? "col-12" : "col-lg-6");

  useEffect(() => {
    if (!lifecycleChartRef.current) return;
    if (lifecycleChart.current) lifecycleChart.current.destroy();
    const ctx = lifecycleChartRef.current.getContext("2d");
    lifecycleChart.current = new Chart(ctx, {
      type: "doughnut",
        data: {
        labels: ["Good", "Fault", "Maintenance Required", "Retired"],
          datasets: [
            {
            data: [metrics.good, metrics.fault, metrics.maintenance, metrics.retired],
            backgroundColor: ["#16a34a", "#ef4444", "#f59e0b", "#94a3b8"],
            borderWidth: 0,
          },
        ],
        },
        options: {
          responsive: true,
        maintainAspectRatio: false,
          plugins: {
          legend: { position: "bottom" },
        },
      },
    });
  }, [metrics.good, metrics.fault, metrics.maintenance, metrics.retired]);

  useEffect(() => {
    if (!sitesChartRef.current) return;
    if (sitesChart.current) sitesChart.current.destroy();
    const ctx = sitesChartRef.current.getContext("2d");
    sitesChart.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: metrics.topSites.map((x) => x[0]),
        datasets: [
          {
            label: "Assets",
            data: metrics.topSites.map((x) => x[1]),
            backgroundColor: THEME,
            borderRadius: 8,
          },
        ],
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          },
          scales: {
          x: { beginAtZero: true, ticks: { precision: 0 } },
        },
      },
    });
  }, [metrics.topSites]);

  useEffect(() => {
    if (!trendChartRef.current) return;
    if (trendChart.current) trendChart.current.destroy();
    const ctx = trendChartRef.current.getContext("2d");
    trendChart.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: metrics.trendLabels,
        datasets: [
          {
            label: "Assets Added",
            data: metrics.trendValues,
            borderColor: THEME,
            backgroundColor: "rgba(139,22,52,0.12)",
            pointRadius: 4,
            tension: 0.35,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          y: { beginAtZero: true, ticks: { precision: 0 } },
        },
      },
    });
  }, [metrics.trendLabels, metrics.trendValues]);

  useEffect(() => {
    if (!ticketChartRef.current) return;
    if (ticketChart.current) ticketChart.current.destroy();
    const ctx = ticketChartRef.current.getContext("2d");
    ticketChart.current = new Chart(ctx, {
      type: "pie",
        data: {
        labels: ["Open", "In Progress", "Closed"],
          datasets: [
            {
            data: [
              metrics.ticketStatusCounts.open,
              metrics.ticketStatusCounts.inProgress,
              metrics.ticketStatusCounts.closed,
            ],
            backgroundColor: [TICKET_OPEN, TICKET_IN_PROGRESS, TICKET_CLOSED],
            borderWidth: 0,
          },
        ],
        },
        options: {
          responsive: true,
        maintainAspectRatio: false,
          plugins: {
          legend: { display: false },
        },
      },
    });
  }, [metrics.ticketStatusCounts]);

  useEffect(() => {
    if (!fatigueSheetStatusChartRef.current) return;
    if (fatigueSheetStatusChart.current) fatigueSheetStatusChart.current.destroy();
    const ctx = fatigueSheetStatusChartRef.current.getContext("2d");
    fatigueSheetStatusChart.current = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: ["Active", "Inactive"],
        datasets: [
          {
            data: [fatigueMetrics.activeSheets, fatigueMetrics.inactiveSheets],
            backgroundColor: ["#16a34a", "#94a3b8"],
            borderWidth: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "bottom" } },
      },
    });
  }, [fatigueMetrics.activeSheets, fatigueMetrics.inactiveSheets]);

  useEffect(() => {
    if (!fatigueShiftPatternChartRef.current) return;
    if (fatigueShiftPatternChart.current) fatigueShiftPatternChart.current.destroy();
    const ctx = fatigueShiftPatternChartRef.current.getContext("2d");
    fatigueShiftPatternChart.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: ["Day Shifts", "Night Shifts"],
        datasets: [
          {
            data: [fatigueMetrics.dayNight.day, fatigueMetrics.dayNight.night],
            backgroundColor: ["#2563eb", "#7c3aed"],
            borderRadius: 8,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
      },
    });
  }, [fatigueMetrics.dayNight.day, fatigueMetrics.dayNight.night]);

  useEffect(() => {
    if (!fatigueExceedanceTypeChartRef.current) return;
    if (fatigueExceedanceTypeChart.current) fatigueExceedanceTypeChart.current.destroy();
    const ctx = fatigueExceedanceTypeChartRef.current.getContext("2d");
    fatigueExceedanceTypeChart.current = new Chart(ctx, {
      type: "pie",
        data: {
        labels: fatigueMetrics.exceedanceTypeRows.map((x) => x[0]),
          datasets: [
            {
            data: fatigueMetrics.exceedanceTypeRows.map((x) => x[1]),
            backgroundColor: ["#8b1634", "#2563eb", "#f59e0b", "#16a34a", "#7c3aed", "#ef4444"],
            borderWidth: 0,
          },
        ],
        },
        options: {
          responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "bottom" } },
      },
    });
  }, [fatigueMetrics.exceedanceTypeRows]);

  useEffect(() => {
    if (!fatigueRiskBandChartRef.current) return;
    if (fatigueRiskBandChart.current) fatigueRiskBandChart.current.destroy();
    const ctx = fatigueRiskBandChartRef.current.getContext("2d");
    fatigueRiskBandChart.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: ["Low (<1.0)", "Medium (1.0-1.29)", "High (1.3-1.59)", "Critical (>=1.6)"],
        datasets: [
          {
            label: "Entries",
            data: [
              fatigueMetrics.riskBands.low,
              fatigueMetrics.riskBands.medium,
              fatigueMetrics.riskBands.high,
              fatigueMetrics.riskBands.critical,
            ],
            backgroundColor: ["#16a34a", "#f59e0b", "#f97316", "#dc2626"],
            borderRadius: 8,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
      },
    });
  }, [fatigueMetrics.riskBands]);

  useEffect(() => {
    return () => {
      if (lifecycleChart.current) lifecycleChart.current.destroy();
      if (sitesChart.current) sitesChart.current.destroy();
      if (trendChart.current) trendChart.current.destroy();
      if (ticketChart.current) ticketChart.current.destroy();
      if (fatigueSheetStatusChart.current) fatigueSheetStatusChart.current.destroy();
      if (fatigueShiftPatternChart.current) fatigueShiftPatternChart.current.destroy();
      if (fatigueExceedanceTypeChart.current) fatigueExceedanceTypeChart.current.destroy();
      if (fatigueRiskBandChart.current) fatigueRiskBandChart.current.destroy();
    };
  }, []);

  const sectionWrap = {
    border: "1px solid #e2e8f0",
    borderRadius: 18,
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)",
    padding: 18,
    marginBottom: 22,
  };

  const sectionHeader = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: "1px solid #e2e8f0",
    flexWrap: "wrap",
  };

  return (
    <div className="container py-4">
      {!hideAssetInsights ? (
      <div style={sectionWrap}>
        <div style={sectionHeader}>
          <h3 style={{ fontWeight: 900, marginBottom: 0, color: THEME, letterSpacing: 0.4, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 30, height: 30, borderRadius: 9, display: "grid", placeItems: "center", background: "#fdf2f8", color: THEME }}>
              <i className="fa-solid fa-boxes-stacked" style={{ fontSize: 14 }} />
            </span>
            Asset Management Insights
          </h3>
        </div>

      <div className="row g-3 mb-3">
        <div className="col-6 col-md-3"><MetricCard title="Total Assets" value={loading ? "..." : metrics.totalAssets} color={THEME} /></div>
        <div className="col-6 col-md-3"><MetricCard title="Maintenance Required" value={loading ? "..." : metrics.maintenance} color="#f59e0b" /></div>
        <div className="col-6 col-md-3"><MetricCard title="Fault Assets" value={loading ? "..." : metrics.fault} color="#ef4444" /></div>
        <div className="col-6 col-md-3"><MetricCard title="Retired Assets" value={loading ? "..." : metrics.retired} color={THEME} /></div>
        <div className="col-6 col-md-3"><MetricCard title="Unassigned QR Labels" value={loading ? "..." : metrics.unassignedLabels} color="#7c3aed" /></div>
        <div className="col-6 col-md-3"><MetricCard title="Open Tickets" value={loading ? "..." : metrics.openTickets} color="#d97706" /></div>
        <div className="col-6 col-md-3"><MetricCard title="In Progress Tickets" value={loading ? "..." : metrics.inProgressTickets} color="#2563eb" /></div>
        <div className="col-6 col-md-3"><MetricCard title="Good Condition Assets" value={loading ? "..." : metrics.good} color="#16a34a" /></div>
      </div>

      <div className="row g-4">
        {hasAssetLifecycleData ? <div className={chartColClass(visibleAssetChartCount)}>
          <ChartCard title="Asset Lifecycle Distribution">
            <canvas ref={lifecycleChartRef} />
          </ChartCard>
        </div> : null}
        {hasTopSitesData ? <div className={chartColClass(visibleAssetChartCount)}>
          <ChartCard title="Top Sites by Asset Count">
            <canvas ref={sitesChartRef} />
          </ChartCard>
        </div> : null}
        {hasAssetTrendData ? <div className={chartColClass(visibleAssetChartCount)}>
          <ChartCard title="Assets Added Trend (Last 6 Months)">
            <canvas ref={trendChartRef} />
          </ChartCard>
        </div> : null}
        {hasTicketStatusData ? <div className={chartColClass(visibleAssetChartCount)}>
          <ChartCard title="Ticket Processing Status">
            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ height: 220 }}>
                <canvas ref={ticketChartRef} />
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Badge label="Open" value={loading ? "..." : metrics.ticketStatusCounts.open} bg="#fff7ed" color={TICKET_OPEN} />
                <Badge label="In Progress" value={loading ? "..." : metrics.ticketStatusCounts.inProgress} bg="#eff6ff" color={TICKET_IN_PROGRESS} />
                <Badge label="Closed" value={loading ? "..." : metrics.ticketStatusCounts.closed} bg="#ecfdf5" color={TICKET_CLOSED} />
        </div>
        </div>
          </ChartCard>
        </div> : null}
        </div>

      <div className="row g-3 mt-1">
        <div className="col-12">
          <div className="card border-0 shadow-sm" style={{ borderRadius: 14, border: "1px solid #e2e8f0" }}>
            <div className="card-body">
              <div style={{ color: "#334155", fontWeight: 700, marginBottom: 10 }}>Top Clients by Asset Allocation</div>
              {metrics.topClients.length === 0 ? (
                <div style={{ color: "#94a3b8", fontSize: 14 }}>No client data available</div>
              ) : (
                metrics.topClients.map(([name, count]) => (
                  <div key={name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                    <span style={{ color: "#334155", fontSize: 14 }}>{name}</span>
                    <span style={{ color: THEME, fontWeight: 700 }}>{count}</span>
        </div>
                ))
              )}
        </div>
        </div>
        </div>
        </div>
        </div>
      ) : null}

      <div style={sectionWrap}>
        <div className="workforce-overview-section-header" style={sectionHeader}>
          <h3 style={{ fontWeight: 900, marginBottom: 0, color: THEME, letterSpacing: 0.4, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 30, height: 30, borderRadius: 9, display: "grid", placeItems: "center", background: "#fdf2f8", color: THEME }}>
              <i className="fa-solid fa-users-gear" style={{ fontSize: 14 }} />
            </span>
            Today&apos;s Workforce Overview
          </h3>
          <WorkforceOverviewFilters
            dateValue={workforceFilterDate}
            workerValue={workforceFilterWorkerId}
            workers={workforceToday.workers}
            rangeLabel={workforceToday.rangeLabel}
            onDateChange={setWorkforceFilterDate}
            onWorkerChange={setWorkforceFilterWorkerId}
            onReset={() => {
              setWorkforceFilterDate(dateToLocalYMD(new Date()));
              setWorkforceFilterWorkerId("");
            }}
          />
        </div>

        <div className="row g-3">
          <div className="col-lg-6">
            <WorkforceStatusColumn
              title="On-Call Standby"
              subtitle="Workers available for emergency callouts"
              count={workforceToday.standbyRows.length}
              countLabel="on standby"
              icon="fa-solid fa-phone-volume"
              accent={THEME}
              loading={loading}
              emptyMessage="No workers on standby"
            >
              {workforceToday.standbyRows.map((row) => (
                <WorkforceStatusItem
                  key={row.id}
                  accent={THEME}
                  badgeLabel="Standby"
                  badgeBg="#fdf2f8"
                  badgeColor={THEME}
                  title={row.workerName}
                  meta={[
                    { icon: "fa-regular fa-calendar", text: row.dateLabel },
                    { icon: "fa-solid fa-location-dot", text: row.siteName },
                    row.siteLocation ? { icon: "fa-regular fa-map", text: row.siteLocation } : null,
                  ].filter(Boolean)}
                />
              ))}
            </WorkforceStatusColumn>
          </div>

          <div className="col-lg-6">
            <WorkforceStatusColumn
              title="Holiday & Absence"
              subtitle="Marked from Work Sheets (Sick / Holiday / College)"
              count={workforceToday.holidayRows.length}
              countLabel="marked"
              icon="fa-solid fa-calendar-xmark"
              accent="#c2410c"
              loading={loading}
              emptyMessage="No sick, holiday or college days marked"
            >
              {workforceToday.holidayRows.map((row) => {
                const typeStyle = DAY_TYPE_COLORS[row.dayType] || DAY_TYPE_COLORS.holiday;
                return (
                  <WorkforceStatusItem
                    key={row.id}
                    accent={typeStyle.color}
                    badgeLabel={row.dayTypeLabel}
                    badgeBg={typeStyle.bg}
                    badgeColor={typeStyle.color}
                    badgeBorder={typeStyle.border}
                    title={row.workerName}
                    meta={[
                      { icon: "fa-regular fa-calendar", text: row.dateLabel },
                      { icon: "fa-solid fa-location-dot", text: row.siteName },
                      row.sheetName ? { icon: "fa-solid fa-clipboard-list", text: row.sheetName } : null,
                      row.reason ? { icon: "fa-solid fa-comment-dots", text: row.reason } : null,
                    ].filter(Boolean)}
                  />
                );
              })}
            </WorkforceStatusColumn>
          </div>
        </div>
      </div>

      <div style={sectionWrap}>
        <div style={sectionHeader}>
          <h3 style={{ fontWeight: 900, marginBottom: 0, color: THEME, letterSpacing: 0.4, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 30, height: 30, borderRadius: 9, display: "grid", placeItems: "center", background: "#fdf2f8", color: THEME }}>
              <i className="fa-solid fa-user-clock" style={{ fontSize: 14 }} />
            </span>
            Fatigue Management Insights
          </h3>
        </div>

      <div className="row g-3 mb-3">
        <div className="col-6 col-md-3"><MetricCard title="Work Sheets" value={loading ? "..." : fatigueMetrics.totalSheets} color={THEME} /></div>
        <div className="col-6 col-md-3"><MetricCard title="Active Sheets" value={loading ? "..." : fatigueMetrics.activeSheets} color="#16a34a" /></div>
        <div className="col-6 col-md-3"><MetricCard title="Shift Records" value={loading ? "..." : fatigueMetrics.totalShifts} color="#2563eb" /></div>
        <div className="col-6 col-md-3"><MetricCard title="Workers (Assigned)" value={loading ? "..." : fatigueMetrics.uniqueWorkersInSheets} color="#7c3aed" /></div>
        <div className="col-6 col-md-3"><MetricCard title="Exceedance Events" value={loading ? "..." : fatigueMetrics.totalExceedances} color="#ef4444" /></div>
        <div className="col-6 col-md-3"><MetricCard title="Pending Exceedance" value={loading ? "..." : fatigueMetrics.pendingExceedances} color="#d97706" /></div>
        <div className="col-6 col-md-3"><MetricCard title="Avg Fatigue Index" value={loading ? "..." : fatigueMetrics.avgFatigueIndex} color="#0f766e" /></div>
        <div className="col-6 col-md-3"><MetricCard title="Avg Shift Hours" value={loading ? "..." : fatigueMetrics.avgShiftHours} color="#334155" /></div>
      </div>

      <div className="row g-4">
        {hasFatigueSheetStatusData ? <div className={chartColClass(visibleFatigueChartCount)}>
          <ChartCard title="Work Sheet Status Distribution">
            <canvas ref={fatigueSheetStatusChartRef} />
          </ChartCard>
        </div> : null}
        {hasFatigueShiftPatternData ? <div className={chartColClass(visibleFatigueChartCount)}>
          <ChartCard title="Shift Pattern Split (Day / Night)">
            <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 14, alignItems: "stretch" }}>
              <div style={{ height: 220 }}>
                <canvas ref={fatigueShiftPatternChartRef} />
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", background: "#f8fafc" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                    <span style={{ color: "#334155", fontWeight: 700 }}>Day shifts</span>
                    <span style={{ color: "#1d4ed8", fontWeight: 800 }}>
                      {fatigueMetrics.dayNight.day}
                      {fatigueMetrics.totalShifts ? ` (${Math.round((fatigueMetrics.dayNight.day / fatigueMetrics.totalShifts) * 100)}%)` : ""}
                    </span>
                  </div>
                  <div style={{ marginTop: 7, height: 7, borderRadius: 999, background: "#dbeafe", overflow: "hidden" }}>
                    <div
                      style={{
                        height: "100%",
                        width: `${fatigueMetrics.totalShifts ? (fatigueMetrics.dayNight.day / fatigueMetrics.totalShifts) * 100 : 0}%`,
                        background: "#2563eb",
                      }}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 12, color: "#475569" }}>
                    <span>Total hours</span>
                    <strong style={{ color: "#1d4ed8" }}>{fatigueMetrics.dayNightHours.day.toFixed(1)}h</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 12, color: "#475569" }}>
                    <span>Avg shift length</span>
                    <strong style={{ color: "#1d4ed8" }}>
                      {fatigueMetrics.dayNightDurationCount.day
                        ? (fatigueMetrics.dayNightHours.day / fatigueMetrics.dayNightDurationCount.day).toFixed(1)
                        : "0.0"}
                      h
                    </strong>
                  </div>
                </div>

                <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", background: "#faf5ff" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                    <span style={{ color: "#334155", fontWeight: 700 }}>Night shifts</span>
                    <span style={{ color: "#7c3aed", fontWeight: 800 }}>
                      {fatigueMetrics.dayNight.night}
                      {fatigueMetrics.totalShifts ? ` (${Math.round((fatigueMetrics.dayNight.night / fatigueMetrics.totalShifts) * 100)}%)` : ""}
                    </span>
                  </div>
                  <div style={{ marginTop: 7, height: 7, borderRadius: 999, background: "#ede9fe", overflow: "hidden" }}>
                    <div
                      style={{
                        height: "100%",
                        width: `${fatigueMetrics.totalShifts ? (fatigueMetrics.dayNight.night / fatigueMetrics.totalShifts) * 100 : 0}%`,
                        background: "#7c3aed",
                      }}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 12, color: "#475569" }}>
                    <span>Total hours</span>
                    <strong style={{ color: "#7c3aed" }}>{fatigueMetrics.dayNightHours.night.toFixed(1)}h</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 12, color: "#475569" }}>
                    <span>Avg shift length</span>
                    <strong style={{ color: "#7c3aed" }}>
                      {fatigueMetrics.dayNightDurationCount.night
                        ? (fatigueMetrics.dayNightHours.night / fatigueMetrics.dayNightDurationCount.night).toFixed(1)
                        : "0.0"}
                      h
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </ChartCard>
        </div> : null}
        {hasFatigueExceedanceTypeData ? <div className={chartColClass(visibleFatigueChartCount)}>
          <ChartCard title="Exceedance Form Types (SF-008-A/B/C/D)">
            <canvas ref={fatigueExceedanceTypeChartRef} />
          </ChartCard>
        </div> : null}
        {hasFatigueRiskBandData ? <div className={chartColClass(visibleFatigueChartCount)}>
          <ChartCard title="Risk Index Band Distribution">
            <canvas ref={fatigueRiskBandChartRef} />
          </ChartCard>
        </div> : null}
        <div className={chartColClass(visibleFatigueChartCount)}>
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14, border: "1px solid #e2e8f0" }}>
            <div className="card-body">
              <div style={{ color: "#334155", fontWeight: 700, marginBottom: 10 }}>Worker ↔ Exceedance Relationship</div>
              {fatigueMetrics.topWorkersByExceedance.length === 0 ? (
                <div style={{ color: "#94a3b8", fontSize: 14 }}>No exceedance data available</div>
              ) : (
                fatigueMetrics.topWorkersByExceedance.map((row) => (
                  <div key={row.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                    <span style={{ color: "#334155", fontSize: 14 }}>{row.name}</span>
                    <span style={{ color: "#b91c1c", fontWeight: 700 }}>{row.count}</span>
                  </div>
                ))
              )}
              <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                <Badge label="Approved" value={loading ? "..." : fatigueMetrics.approvedExceedances} bg="#ecfdf5" color="#166534" />
                <Badge label="Rejected" value={loading ? "..." : fatigueMetrics.rejectedExceedances} bg="#fef2f2" color="#b91c1c" />
                <Badge label="On Rest Entries" value={loading ? "..." : fatigueMetrics.onRestCount} bg="#eff6ff" color="#1d4ed8" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mt-1">
        <div className="col-12">
          <div className="card border-0 shadow-sm" style={{ borderRadius: 14, border: "1px solid #e2e8f0" }}>
            <div className="card-body">
              <div style={{ color: "#334155", fontWeight: 700, marginBottom: 10 }}>Top Workers by Shift Entries</div>
              {fatigueMetrics.topWorkersByShifts.length === 0 ? (
                <div style={{ color: "#94a3b8", fontSize: 14 }}>No shift data available</div>
              ) : (
                fatigueMetrics.topWorkersByShifts.map((row) => (
                  <div key={row.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                    <span style={{ color: "#334155", fontSize: 14 }}>{row.name}</span>
                    <span style={{ color: THEME, fontWeight: 700 }}>{row.count}</span>
                  </div>
                ))
              )}
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value, color }) {
  const iconByTitle = {
    "Total Assets": "fa-solid fa-boxes-stacked",
    "Maintenance Required": "fa-solid fa-screwdriver-wrench",
    "Fault Assets": "fa-solid fa-triangle-exclamation",
    "Retired Assets": "fa-solid fa-box-archive",
    "Unassigned QR Labels": "fa-solid fa-qrcode",
    "Open Tickets": "fa-solid fa-folder-open",
    "In Progress Tickets": "fa-solid fa-spinner",
    "Good Condition Assets": "fa-solid fa-circle-check",
    "Work Sheets": "fa-solid fa-clipboard-list",
    "Active Sheets": "fa-solid fa-circle-play",
    "Shift Records": "fa-solid fa-business-time",
    "Workers (Assigned)": "fa-solid fa-users",
    "Exceedance Events": "fa-solid fa-file-circle-exclamation",
    "Pending Exceedance": "fa-solid fa-hourglass-half",
    "Avg Fatigue Index": "fa-solid fa-gauge-high",
    "Avg Shift Hours": "fa-regular fa-clock",
  };

  const icon = iconByTitle[title] || "fa-solid fa-chart-simple";

  return (
    <div
      className="card border-0 h-100"
      style={{
        borderRadius: 16,
        background: "linear-gradient(145deg, #ffffff 0%, #fbfdff 100%)",
        border: "1px solid #e2e8f0",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.07)",
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
      }}
    >
      <div className="card-body py-3 px-3" style={{ position: "relative", overflow: "hidden" }}>
        <div
          style={{
            position: "absolute",
            top: -20,
            right: -20,
            width: 88,
            height: 88,
            borderRadius: "50%",
            background: `${color}1A`,
          }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, position: "relative", zIndex: 1 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#64748b", letterSpacing: "0.06em", textTransform: "uppercase", lineHeight: 1.35 }}>{title}</div>
            <div style={{ fontSize: 30, fontWeight: 900, color, marginTop: 8, lineHeight: 1.05 }}>{value}</div>
          </div>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              display: "grid",
              placeItems: "center",
              background: `${color}1A`,
              color,
              flexShrink: 0,
            }}
          >
            <i className={icon} style={{ fontSize: 14 }} />
          </div>
        </div>
        <div style={{ marginTop: 10, height: 4, borderRadius: 999, background: "#e2e8f0", overflow: "hidden", position: "relative", zIndex: 1 }}>
          <div style={{ height: "100%", width: "62%", background: color }} />
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14, border: "1px solid #e2e8f0" }}>
      <div className="card-body">
        <div style={{ color: "#334155", fontWeight: 700, marginBottom: 10 }}>{title}</div>
        <div style={{ height: 280 }}>{children}</div>
      </div>
    </div>
  );
}

function Badge({ label, value, bg, color }) {
  return (
    <div style={{ background: bg, color, borderRadius: 999, padding: "8px 14px", fontWeight: 700, fontSize: 14 }}>
      {label}: {value}
    </div>
  );
}

function WorkforceOverviewFilters({
  dateValue,
  workerValue,
  workers,
  rangeLabel,
  onDateChange,
  onWorkerChange,
  onReset,
}) {
  const [workerDropdownOpen, setWorkerDropdownOpen] = useState(false);
  const workerDropdownRef = useRef(null);
  const todayYmd = dateToLocalYMD(new Date());
  const hasFilters = Boolean(workerValue) || (Boolean(dateValue) && dateValue !== todayYmd);
  const workerList = Array.isArray(workers) ? workers : [];
  const selectedWorker = workerList.find((worker) => worker.id === workerValue) || null;

  useEffect(() => {
    if (!workerDropdownOpen) return undefined;
    function handleClickOutside(event) {
      if (workerDropdownRef.current && !workerDropdownRef.current.contains(event.target)) {
        setWorkerDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [workerDropdownOpen]);

  return (
    <div className="workforce-overview-filters">
      {!hasFilters ? (
        <span className="workforce-overview-range-label">{rangeLabel}</span>
      ) : null}
      <div className="workforce-overview-filter-controls">
        <div className="site-register-datepicker-wrap workforce-overview-date-filter">
          <DatePicker
            selected={parseWorkforceFilterDate(dateValue)}
            onChange={(d) => onDateChange(d ? dateToLocalYMD(d) : "")}
            dateFormat="dd-MM-yyyy"
            className="form-control form-control-sm workforce-overview-date-input"
            placeholderText="dd-MM-yyyy"
            isClearable
            todayButton="Today"
            calendarClassName="site-register-theme-calendar"
            popperClassName="site-register-datepicker-popper"
            popperPlacement="bottom-end"
            popperProps={{ strategy: "fixed" }}
          />
        </div>
        <div ref={workerDropdownRef} className="workforce-overview-worker-select">
          <button
            type="button"
            className={`workforce-overview-worker-trigger${workerDropdownOpen ? " is-open" : ""}`}
            onClick={() => setWorkerDropdownOpen((open) => !open)}
            aria-haspopup="listbox"
            aria-expanded={workerDropdownOpen}
          >
            <span className="workforce-overview-worker-trigger-label">
              {selectedWorker?.name || "All workers"}
            </span>
            <i className={`fa-solid fa-chevron-${workerDropdownOpen ? "up" : "down"}`} />
          </button>
          {workerDropdownOpen ? (
            <div className="workforce-overview-worker-menu" role="listbox" onMouseDown={(e) => e.preventDefault()}>
              <button
                type="button"
                className={`workforce-overview-worker-option${workerValue === "" ? " is-selected" : ""}`}
                onClick={() => {
                  onWorkerChange("");
                  setWorkerDropdownOpen(false);
                }}
              >
                All workers
              </button>
              {workerList.map((worker) => (
                <button
                  key={worker.id}
                  type="button"
                  className={`workforce-overview-worker-option${workerValue === worker.id ? " is-selected" : ""}`}
                  onClick={() => {
                    onWorkerChange(worker.id);
                    setWorkerDropdownOpen(false);
                  }}
                >
                  {worker.name}
                </button>
              ))}
              {workerList.length === 0 ? (
                <div className="workforce-overview-worker-empty">No workers found</div>
              ) : null}
            </div>
          ) : null}
        </div>
        {hasFilters ? (
          <button type="button" className="workforce-overview-reset-btn" onClick={onReset}>
            Reset
          </button>
        ) : null}
      </div>
    </div>
  );
}

function WorkforceStatusColumn({ title, subtitle, count, countLabel, icon, accent, loading, emptyMessage, children }) {
  return (
    <div
      className="h-100"
      style={{
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        background: "linear-gradient(180deg, #ffffff 0%, #fcfdff 100%)",
        boxShadow: "0 8px 22px rgba(15, 23, 42, 0.05)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          padding: "16px 18px",
          borderBottom: "1px solid #f1f5f9",
          background: `linear-gradient(135deg, ${accent}10 0%, #ffffff 100%)`,
        }}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", minWidth: 0 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              display: "grid",
              placeItems: "center",
              background: `${accent}18`,
              color: accent,
              flexShrink: 0,
            }}
          >
            <i className={icon} style={{ fontSize: 16 }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, color: "#1e293b", fontSize: 16, lineHeight: 1.25 }}>{title}</div>
            <div style={{ color: "#64748b", fontSize: 12, marginTop: 4, lineHeight: 1.4 }}>{subtitle}</div>
          </div>
        </div>
        <div
          style={{
            textAlign: "right",
            flexShrink: 0,
            padding: "4px 10px",
            borderRadius: 999,
            background: `${accent}12`,
            border: `1px solid ${accent}25`,
          }}
        >
          <div style={{ fontSize: 20, fontWeight: 900, color: accent, lineHeight: 1 }}>{loading ? "…" : count}</div>
          <div style={{ fontSize: 10, fontWeight: 700, color: accent, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 2 }}>
            {countLabel}
          </div>
        </div>
      </div>

      <div style={{ padding: "12px 14px 14px", maxHeight: 360, overflowY: "auto" }}>
        {loading ? (
          <div style={{ color: "#94a3b8", fontSize: 14, padding: "18px 8px", textAlign: "center" }}>Loading…</div>
        ) : count === 0 ? (
          <div
            style={{
              color: "#94a3b8",
              fontSize: 14,
              padding: "24px 12px",
              textAlign: "center",
              border: "1px dashed #e2e8f0",
              borderRadius: 12,
              background: "#f8fafc",
            }}
          >
            <i className="fa-regular fa-circle-check" style={{ display: "block", fontSize: 22, marginBottom: 8, color: "#cbd5e1" }} />
            {emptyMessage}
          </div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>{children}</div>
        )}
      </div>
    </div>
  );
}

function WorkforceStatusItem({ accent, badgeLabel, badgeBg, badgeColor, badgeBorder, title, meta }) {
  return (
    <div
      style={{
        border: `1px solid ${badgeBorder || "#e2e8f0"}`,
        borderRadius: 12,
        padding: "12px 14px",
        background: "#fff",
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, color: "#1e293b", fontSize: 15, lineHeight: 1.3 }}>{title}</div>
          <div style={{ display: "grid", gap: 5, marginTop: 8 }}>
            {meta.map((item, index) => (
              <div key={`${item.text}-${index}`} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#475569", lineHeight: 1.35 }}>
                <i className={item.icon} style={{ color: accent, marginTop: 2, width: 14, textAlign: "center", flexShrink: 0 }} />
                <span style={{ wordBreak: "break-word" }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
        <span
          style={{
            flexShrink: 0,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.03em",
            textTransform: "uppercase",
            color: badgeColor,
            background: badgeBg,
            border: `1px solid ${badgeBorder || `${badgeColor}30`}`,
            borderRadius: 999,
            padding: "5px 10px",
            whiteSpace: "nowrap",
          }}
        >
          {badgeLabel}
        </span>
      </div>
    </div>
  );
}

export default DashboardMain;
